import { Globe, RefreshCw } from "lucide-react";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useClientSelection } from "../client-context";
import SiteCard from "../components/web/SiteCard";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "../components/Ui";
import { useApi } from "../hooks";
import { sortOverview, type OverviewEntry, type OverviewSort } from "../web-analytics";

const SORTS: { value: OverviewSort; label: string; title: string }[] = [
  { value: "traffic", label: "Traffic", title: "Most visitors in the last 7 days first" },
  { value: "change", label: "Change", title: "Biggest rise on the week before first" },
  { value: "name", label: "Name", title: "By client, then site" },
];

/**
 * Agency "Website visitors": every client site with the tracking script, ported from
 * Sonar's OverviewDashboard. Down sites always come first. The sort lives in
 * the URL (`?sort=`), so a view can be shared.
 */
export default function Websites() {
  const overview = useApi<OverviewEntry[]>("/api/web/overview");
  const { clients } = useClientSelection();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const raw = params.get("sort");
  const sort: OverviewSort = raw === "change" || raw === "name" ? raw : "traffic";
  const sites = useMemo(() => sortOverview(overview.data || [], sort), [overview.data, sort]);
  const down = sites.filter((e) => e.uptime_state === "down").length;

  const setSort = (value: OverviewSort) => {
    const next = new URLSearchParams(params);
    if (value === "traffic") next.delete("sort");
    else next.set("sort", value);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader
        title="Website visitors"
        actions={<button className="button secondary" disabled={overview.refreshing} onClick={overview.reload}><RefreshCw className={overview.refreshing ? "spin" : ""} aria-hidden="true" />{overview.refreshing ? "Refreshing…" : "Refresh"}</button>}
      />
      {overview.loading ? (
        <LoadingState label="Loading websites" />
      ) : overview.error ? (
        <ErrorState message={overview.error} onRetry={overview.reload} />
      ) : !sites.length ? (
        <EmptyState
          icon={<Globe aria-hidden="true" />}
          title="No client site sends visitors yet"
          description="Set up tracking on a client's Tracking page and paste the snippet into their site. Visitors show here from the first visit."
          action={
            <label className="select-control ws-pick-client">
              <span className="sr-only">Open a client's Tracking page</span>
              <select value="" onChange={(e) => { if (e.target.value) navigate(`/tracking?client_id=${e.target.value}`); }}>
                <option value="">Set up a client…</option>
                {(clients.data || []).filter((c) => c.status === "active").map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          }
        />
      ) : (
        <>
          <div className="ws-toolbar">
            <p className="ws-count" aria-live="polite">
              {sites.length} {sites.length === 1 ? "site" : "sites"}
              {down > 0 && <span className="ws-count-down">, {down} down</span>}
            </p>
            <div className="segmented-control compact" role="group" aria-label="Sort sites">
              {SORTS.map((s) => (
                <button key={s.value} type="button" title={s.title} aria-pressed={sort === s.value}
                  className={sort === s.value ? "active" : ""} onClick={() => setSort(s.value)}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="ws-grid">
            {sites.map((entry) => <SiteCard key={entry.site.id} entry={entry} />)}
          </div>
        </>
      )}
    </>
  );
}
